import {
  BottomNav,
  Button,
  DevBanner,
  SlinguAvatar,
  SlinguGuide,
  type SlinguMood,
  Icons,
  PlatformPath,
  LessonPlayer,
  LessonSummary,
  Logo,
  MissionCard,
  type PlayerExercise,
  SectionHeader,
  SfxToggle,
  Sidebar,
  StatsBar,
  UnitBanner,
  useSfx,
} from '@slingu/ui';
import { useEffect, useMemo, useRef, useState } from 'react';

import { pickIllustration } from '@slingu/ui/illustrations';
import type { ComponentProps } from 'react';

import { SECTIONS } from './curriculum/index.js';
import { CHARACTER_META, LEAGUE_TIERS, VOICE_IDS, type Cefr } from './curriculum/types.js';
import type { Lesson, Section, Unit } from './curriculum/types.js';
import { Landing } from './Landing.js';
import { CarRacer } from './minigame/CarRacer.js';
import { FlappySlingu } from './minigame/FlappySlingu.js';
import { Hangman } from './minigame/Hangman.js';
import { QuizRun, type QuizRunQuestion } from './minigame/QuizRun.js';
import { WordBuilder } from './minigame/WordBuilder.js';
import { WordJumper } from './minigame/WordJumper.js';
import { WordMatch } from './minigame/WordMatch.js';
import { WordMines } from './minigame/WordMines.js';
import { WordSearch } from './minigame/WordSearch.js';
import { WordSlots } from './minigame/WordSlots.js';
import {
  PlacementQuiz,
  PlacementResult,
  type PlacementOutcome,
} from './PlacementQuiz.js';

import './app.css';

type SessionUser = {
  userId: string;
  email: string;
  roles: string[];
  permissions: string[];
};

type Route = 'landing' | 'app';

type Progress = {
  completedLessons: string[];
  xp: number;
  streak: number;
  /** YYYY-MM-DD do último dia com atividade (lição/minigame). Usado pra decidir
   * se o streak diário incrementa, mantém ou reseta. */
  lastActivityDate: string | null;
  hearts: number;
  gems: number;
  activeUnit: string;
  activeSection: string;
  reviews: Record<string, number>;
  weakSpots: Record<string, number>;
  placement?: { level: Cefr; takenAt: number } | null;
};

const PROGRESS_KEY = 'slingu:progress:v1';

function todayKey(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function daysBetween(aKey: string, bKey: string): number {
  const a = new Date(`${aKey}T00:00:00`);
  const b = new Date(`${bKey}T00:00:00`);
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

/**
 * Regra de streak diário (spec: docs/gamification/overview.md §2):
 *   - primeira atividade de qualquer dia        → streak = 1
 *   - atividade no dia seguinte ao último       → streak + 1
 *   - atividade no mesmo dia do último registro → streak inalterado
 *   - lacuna ≥ 2 dias                           → streak = 1 (reset)
 * Retorna o novo streak + data pra persistir.
 */
function bumpDailyStreak(
  currentStreak: number,
  lastActivityDate: string | null,
  today: string = todayKey(),
): { streak: number; lastActivityDate: string } {
  if (!lastActivityDate) return { streak: 1, lastActivityDate: today };
  const gap = daysBetween(lastActivityDate, today);
  if (gap === 0) return { streak: currentStreak, lastActivityDate };
  if (gap === 1) return { streak: currentStreak + 1, lastActivityDate: today };
  return { streak: 1, lastActivityDate: today };
}

// Unit accent colors — light theme friendly. Each unit picks one of these
// to theme its banner + path nodes. All contrast well against white.
const UNIT_COLOR_MAP: Record<string, string> = {
  owl: 'rgb(65, 161, 2)',           // deep green
  beetle: 'rgb(155, 89, 230)',      // purple
  peacock: 'rgb(0, 167, 128)',      // teal
  macaw: 'rgb(28, 176, 246)',       // brand blue
  fox: 'rgb(255, 140, 0)',          // orange
  cardinal: 'rgb(238, 85, 85)',     // red
  starfish: 'rgb(224, 64, 156)',    // pink
  butterfly: 'rgb(111, 78, 161)',   // deep purple
  kiwi: 'rgb(73, 142, 45)',         // olive green
};

const NAV = [
  { key: 'home', label: 'Aprender', icon: <Icons.HomeIcon size={26} /> },
  { key: 'games', label: 'Jogos', icon: <Icons.GamepadIcon size={26} /> },
  { key: 'missions', label: 'Missões', icon: <Icons.TargetIcon size={26} />, badge: 3 },
  { key: 'leagues', label: 'Ligas', icon: <Icons.TrophyIcon size={26} /> },
  { key: 'profile', label: 'Perfil', icon: <Icons.UserIcon size={26} /> },
];

// ---- Spaced Repetition (SM-2 lite) -----------------------------------------
// Interval by consecutive mastery count. After 5th perfect pass we treat the
// lesson as mastered (~ one-month review).
const SRS_INTERVALS_DAYS = [1, 3, 7, 14, 30, 60];

function scheduleReview(masteryCount: number): number {
  const i = Math.min(SRS_INTERVALS_DAYS.length - 1, Math.max(0, masteryCount - 1));
  const days = SRS_INTERVALS_DAYS[i]!;
  return Date.now() + days * 24 * 60 * 60 * 1000;
}

function loadProgress(): Progress {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Progress;
      return {
        completedLessons: parsed.completedLessons ?? [],
        xp: parsed.xp ?? 0,
        streak: parsed.streak ?? 0,
        lastActivityDate: parsed.lastActivityDate ?? null,
        hearts: parsed.hearts ?? 5,
        gems: parsed.gems ?? 25,
        activeSection: parsed.activeSection ?? SECTIONS[0]!.id,
        activeUnit: parsed.activeUnit ?? SECTIONS[0]!.units[0]!.id,
        reviews: parsed.reviews ?? {},
        weakSpots: parsed.weakSpots ?? {},
      };
    }
  } catch {
    // ignore
  }
  return {
    completedLessons: [],
    xp: 0,
    streak: 0,
    lastActivityDate: null,
    hearts: 5,
    gems: 25,
    activeSection: SECTIONS[0]!.id,
    activeUnit: SECTIONS[0]!.units[0]!.id,
    reviews: {},
    weakSpots: {},
    placement: null,
  };
}

function saveProgress(p: Progress): void {
  try {
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(p));
  } catch {
    // ignore
  }
}

function computeLevel(xp: number): number {
  return Math.floor(xp / 100) + 1;
}

function renderIllustrationFor(theme: string | undefined, size: number) {
  const Comp = pickIllustration(theme) as React.FC<ComponentProps<'svg'> & { size?: number }>;
  return <Comp size={size} />;
}

// Banco de perguntas do QuizRun — dificuldade 1→3 escalando.
const QUIZRUN_QUESTIONS: QuizRunQuestion[] = [
  // 1 · A1 (fácil)
  { id: 'qr1', prompt: '"Hello" significa…', choices: ['Olá', 'Adeus'], answerIndex: 0, difficulty: 1 },
  { id: 'qr2', prompt: 'Pronome para "ela":', choices: ['she', 'he'], answerIndex: 0, difficulty: 1 },
  { id: 'qr3', prompt: '"Red" é…', choices: ['Vermelho', 'Azul'], answerIndex: 0, difficulty: 1 },
  { id: 'qr4', prompt: '"Water":', choices: ['Água', 'Pão'], answerIndex: 0, difficulty: 1 },
  { id: 'qr5', prompt: 'Verbo to be para "I":', choices: ['am', 'is'], answerIndex: 0, difficulty: 1 },
  // 2 · A2
  { id: 'qr6', prompt: 'Past of "go":', choices: ['went', 'goed'], answerIndex: 0, difficulty: 2 },
  { id: 'qr7', prompt: '"___ you like pizza?"', choices: ['Do', 'Does'], answerIndex: 0, difficulty: 2 },
  { id: 'qr8', prompt: 'Past of "see":', choices: ['saw', 'seen'], answerIndex: 0, difficulty: 2 },
  { id: 'qr9', prompt: '"How ___ apples?"', choices: ['many', 'much'], answerIndex: 0, difficulty: 2 },
  { id: 'qr10', prompt: 'Comparativo de "good":', choices: ['better', 'gooder'], answerIndex: 0, difficulty: 2 },
  // 3 · B1+
  { id: 'qr11', prompt: '"If it rains, I ___ stay":', choices: ['will', 'would'], answerIndex: 0, difficulty: 3 },
  { id: 'qr12', prompt: '"She has ___ here for years":', choices: ['lived', 'lives'], answerIndex: 0, difficulty: 3 },
  { id: 'qr13', prompt: 'Softer: "You ___ be wrong":', choices: ['might', 'must'], answerIndex: 0, difficulty: 3 },
  { id: 'qr14', prompt: 'Passive: "The book ___ written":', choices: ['was', 'had'], answerIndex: 0, difficulty: 3 },
  { id: 'qr15', prompt: 'Idiom "piece of cake":', choices: ['fácil', 'saboroso'], answerIndex: 0, difficulty: 3 },
];

// Flappy Slingu agora carrega vocabulário por TEMA (ver minigame/themes.ts).
// O jogador escolhe o tema antes de começar; cada tema tem ~100 palavras.

export function App() {
  const [route, setRoute] = useState<Route>('landing');
  const [active, setActive] = useState<string>('home');
  const [user, setUser] = useState<SessionUser | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [devBypass, setDevBypass] = useState(false);
  const [progress, setProgress] = useState<Progress>(() => loadProgress());
  const [playingLesson, setPlayingLesson] = useState<Lesson | null>(null);
  const [summary, setSummary] = useState<null | {
    lesson: Lesson;
    stats: { correctCount: number; totalCount: number; streak: number; xpEarned: number };
  }>(null);
  const [pendingPlacement, setPendingPlacement] = useState<PlacementOutcome | null>(null);
  const [guideMinimized, setGuideMinimized] = useState(false);
  const [playingMinigame, setPlayingMinigame] = useState(false);
  const [playingQuizRun, setPlayingQuizRun] = useState(false);
  const [playingCarRacer, setPlayingCarRacer] = useState(false);
  const [playingWordMatch, setPlayingWordMatch] = useState(false);
  const [playingWordBuilder, setPlayingWordBuilder] = useState(false);
  const [playingHangman, setPlayingHangman] = useState(false);
  const [playingWordMines, setPlayingWordMines] = useState(false);
  const [playingWordSlots, setPlayingWordSlots] = useState(false);
  const [playingWordSearch, setPlayingWordSearch] = useState(false);
  const [playingWordJumper, setPlayingWordJumper] = useState(false);

  // Guard de idempotência para XP de minigames. Alguns minigames chamam onExit
  // em 2 lugares (botão × + botão final); sem isso, clique duplo concede XP 2x.
  // Reset ao abrir o minigame, set ao conceder XP; 2º award é descartado.
  const minigameAwardedRef = useRef(false);
  const awardMinigameXp = (xp: number) => {
    if (minigameAwardedRef.current) return;
    minigameAwardedRef.current = true;
    if (xp > 0) setProgress((p) => ({ ...p, xp: p.xp + xp }));
  };
  const openMinigame = (set: (v: boolean) => void) => {
    minigameAwardedRef.current = false;
    set(true);
  };

  // Mensagem + humor do Slingu baseado na rota atual.
  const { message: guideMessage, mood: guideMood } = useMemo<{
    message: string;
    mood: SlinguMood;
  }>(() => {
    if (playingLesson) return { message: 'Manda ver! Eu tô aqui torcendo 🫧', mood: 'focus' };
    if (pendingPlacement)
      return { message: 'Seu nível tá pronto! Bora começar?', mood: 'happy' };
    if (active === 'missions')
      return { message: 'Missões diárias dão XP extra. Olha aquelas!', mood: 'wave' };
    if (active === 'leagues')
      return { message: 'Top 3 sobe de liga no domingo. Tu consegue.', mood: 'wave' };
    if (active === 'profile')
      return { message: 'Você tá indo bem. Segue firme!', mood: 'happy' };
    if (progress.completedLessons.length === 0)
      return { message: 'Oi! Sou o Slingu. Toca na plataforma brilhante pra começar.', mood: 'wave' };
    return { message: 'Sua próxima lição tá ali 👇 Manda ver!', mood: 'wave' };
  }, [active, playingLesson, pendingPlacement, progress.completedLessons.length]);

  function handlePlacement(outcome: PlacementOutcome) {
    setProgress((p) => ({
      ...p,
      activeSection: outcome.suggestedSectionId,
      activeUnit: outcome.suggestedUnitId,
      placement: { level: outcome.level, takenAt: Date.now() },
    }));
    setPendingPlacement(outcome);
  }

  async function refreshSession(): Promise<SessionUser | null> {
    // Guest-only mode: BFF auth is disabled for now. Every visitor gets a
    // local learner session; progress lives in localStorage.
    const guest: SessionUser = {
      userId: 'guest',
      email: 'guest@slingu.app',
      roles: ['learner'],
      permissions: ['lesson:play', 'gamification:read'],
    };
    setUser(guest);
    return guest;
  }

  function dismissPlacement() {
    setPendingPlacement(null);
    setActive('home');
  }

  useEffect(() => saveProgress(progress), [progress]);

  useEffect(() => {
    void (async () => {
      await refreshSession();
      setAuthChecked(true);
      // Keep the new visitor on the Landing screen until they press Iniciar.
      // Returning users (progress exists) skip straight to the app.
      if (progress.completedLessons.length > 0 || progress.placement) {
        setRoute('app');
      }
    })();
  }, []);

  const completedSet = useMemo(() => new Set(progress.completedLessons), [progress.completedLessons]);
  const currentSection = SECTIONS.find((s) => s.id === progress.activeSection) ?? SECTIONS[0]!;
  const currentUnit = currentSection.units.find((u) => u.id === progress.activeUnit) ?? currentSection.units[0]!;

  const reviewsDue = useMemo(() => {
    const now = Date.now();
    return Object.entries(progress.reviews)
      .filter(([, ts]) => ts <= now)
      .map(([id]) => id);
  }, [progress.reviews]);

  function lessonStatus(
    unit: Unit,
    lesson: Lesson,
    indexInUnit: number,
  ): 'locked' | 'available' | 'in_progress' | 'completed' {
    if (completedSet.has(lesson.id)) return 'completed';
    if (indexInUnit === 0) return 'available';
    const prevLesson = unit.lessons[indexInUnit - 1];
    if (prevLesson && completedSet.has(prevLesson.id)) return 'available';
    return 'locked';
  }

  function onLessonComplete(lesson: Lesson, stats: {
    correctCount: number;
    totalCount: number;
    streak: number;
    xpEarned: number;
    errors: string[];
  }) {
    setPlayingLesson(null);
    setSummary({ lesson, stats });
    setProgress((p) => {
      const already = p.completedLessons.includes(lesson.id);
      const completed = already ? p.completedLessons : [...p.completedLessons, lesson.id];
      const masteryCount = (p.reviews[lesson.id] ? 1 : 0) + 1;
      const weakSpots = { ...p.weakSpots };
      for (const exId of stats.errors) {
        weakSpots[exId] = (weakSpots[exId] ?? 0) + 1;
      }
      const { streak, lastActivityDate } = bumpDailyStreak(p.streak, p.lastActivityDate);
      return {
        ...p,
        completedLessons: completed,
        xp: p.xp + stats.xpEarned,
        gems: p.gems + Math.floor(stats.xpEarned / 5),
        streak,
        lastActivityDate,
        reviews: { ...p.reviews, [lesson.id]: scheduleReview(masteryCount) },
        weakSpots,
      };
    });
  }

  // Initial loading/auth check.
  if (!authChecked) {
    return (
      <div
        className="sl-loading"
      >
        <Logo size={48} />
      </div>
    );
  }

  // Landing until user presses Iniciar. Login/register disabled for now.
  if (route === 'landing') {
    return (
      <Landing
        onLogin={() => setRoute('app')}
        onRegister={() => setRoute('app')}
      />
    );
  }

  // Authenticated but no placement yet → quiz.
  if (!progress.placement) {
    return <PlacementQuiz onDone={handlePlacement} />;
  }
  if (pendingPlacement) {
    return <PlacementResult outcome={pendingPlacement} onStart={dismissPlacement} />;
  }

  return (
    <div className="sl-app">
      <div className="sl-top-accent" aria-hidden />
      {devBypass ? (
        <div className="sl-app__banner">
          <DevBanner />
        </div>
      ) : null}

      <Sidebar
        title={
          <div className="sl-header__brand">
            <Logo size={36} /> <span>Slingu</span>
          </div>
        }
        items={NAV.map((i) => ({ key: i.key, label: i.label, icon: i.icon }))}
        active={active}
        onSelect={setActive}
        footer={<span>English from Pop Culture · v0.1</span>}
      />

      <main className="sl-main">
        <header className="sl-header">
          <div className="sl-header__brand">
            <Logo size={28} /> <span>Slingu</span>
          </div>
          <StatsBar
            xp={progress.xp}
            streak={progress.streak}
            level={computeLevel(progress.xp)}
            hearts={progress.hearts}
            gems={progress.gems}
          />
          <SfxToggle variant="pill" className="sl-header__sfx" />
        </header>

        {active === 'home' && (
          <HomeView
            section={currentSection}
            unit={currentUnit}
            reviewsDue={reviewsDue}
            onStartLesson={setPlayingLesson}
            onSelectUnit={(sId, uId) =>
              setProgress((p) => ({ ...p, activeSection: sId, activeUnit: uId }))
            }
            lessonStatus={lessonStatus}
          />
        )}

        {active === 'games' && (
          <GamesView
            onPlayMinigame={() => openMinigame(setPlayingMinigame)}
            onPlayQuizRun={() => openMinigame(setPlayingQuizRun)}
            onPlayCarRacer={() => openMinigame(setPlayingCarRacer)}
            onPlayWordMatch={() => openMinigame(setPlayingWordMatch)}
            onPlayWordBuilder={() => openMinigame(setPlayingWordBuilder)}
            onPlayHangman={() => openMinigame(setPlayingHangman)}
            onPlayWordMines={() => openMinigame(setPlayingWordMines)}
            onPlayWordSlots={() => openMinigame(setPlayingWordSlots)}
            onPlayWordSearch={() => openMinigame(setPlayingWordSearch)}
            onPlayWordJumper={() => openMinigame(setPlayingWordJumper)}
          />
        )}
        {active === 'missions' && (
          <MissionsView progress={progress} reviewsDue={reviewsDue.length} />
        )}
        {active === 'leagues' && <LeaguesView xp={progress.xp} />}
        {active === 'profile' && <ProfileView user={user} progress={progress} />}
      </main>

      <BottomNav items={NAV} active={active} onSelect={setActive} />

      {playingLesson ? (
        <LessonPlayer
          exercises={playingLesson.exercises.map((ex, i) => toPlayerExercise(ex, i))}
          hearts={progress.hearts}
          onClose={() => setPlayingLesson(null)}
          onComplete={(stats) => onLessonComplete(playingLesson, stats)}
        />
      ) : null}

      {summary ? (
        <LessonSummary stats={summary.stats} onDone={() => setSummary(null)} />
      ) : null}

      {playingMinigame ? (
        <FlappySlingu
          onExit={(result) => {
            setPlayingMinigame(false);
            awardMinigameXp(result.xp);
          }}
        />
      ) : null}

      {playingCarRacer ? (
        <CarRacer
          onExit={(result) => {
            setPlayingCarRacer(false);
            awardMinigameXp(result.xp);
          }}
        />
      ) : null}

      {playingWordMatch ? (
        <WordMatch
          onExit={(result) => {
            setPlayingWordMatch(false);
            awardMinigameXp(result.xp);
          }}
        />
      ) : null}

      {playingWordBuilder ? (
        <WordBuilder
          onExit={(result) => {
            setPlayingWordBuilder(false);
            awardMinigameXp(result.xp);
          }}
        />
      ) : null}

      {playingHangman ? (
        <Hangman
          onExit={(result) => {
            setPlayingHangman(false);
            awardMinigameXp(result.xp);
          }}
        />
      ) : null}

      {playingWordMines ? (
        <WordMines
          onExit={(result) => {
            setPlayingWordMines(false);
            awardMinigameXp(result.xp);
          }}
        />
      ) : null}

      {playingWordSlots ? (
        <WordSlots
          onExit={(result) => {
            setPlayingWordSlots(false);
            awardMinigameXp(result.xp);
          }}
        />
      ) : null}

      {playingWordSearch ? (
        <WordSearch
          onExit={(result) => {
            setPlayingWordSearch(false);
            awardMinigameXp(result.xp);
          }}
        />
      ) : null}

      {playingWordJumper ? (
        <WordJumper
          onExit={(result) => {
            setPlayingWordJumper(false);
            awardMinigameXp(result.xp);
          }}
        />
      ) : null}

      {playingQuizRun ? (
        <QuizRun
          questions={QUIZRUN_QUESTIONS}
          onExit={(result) => {
            setPlayingQuizRun(false);
            awardMinigameXp(result.xp);
          }}
        />
      ) : null}

      <SlinguGuide
        message={guideMinimized ? undefined : guideMessage}
        mood={guideMinimized ? 'idle' : guideMood}
        minimized={guideMinimized}
        onTap={() => setGuideMinimized(false)}
        onDismiss={guideMinimized ? undefined : () => setGuideMinimized(true)}
      />
    </div>
  );
}

function toPlayerExercise(
  ex: Lesson['exercises'][number],
  order: number,
): PlayerExercise {
  const character = ex.character ? CHARACTER_META[ex.character] : undefined;
  // Voice: honour the exercise's explicit preference, otherwise alternate
  // female/male across the lesson so listening feels like a conversation.
  const voicePreset = ex.voice ?? (order % 2 === 0 ? 'female' : 'male');
  const voiceId = VOICE_IDS[voicePreset];
  const base = {
    id: ex.id,
    newWord: ex.newWord,
    character,
    bubble: ex.bubble,
    keyword: ex.keyword,
    voiceId,
  };
  switch (ex.type) {
    case 'multiple_choice':
      return {
        ...base,
        type: 'multiple_choice',
        prompt: ex.prompt,
        choices: ex.choices,
        answerIndex: ex.answerIndex,
      };
    case 'fill_blank':
      return { ...base, type: 'fill_blank', prompt: ex.prompt, answer: ex.answer, accepts: ex.accepts };
    case 'translate':
      return {
        ...base,
        type: 'translate',
        prompt: ex.prompt,
        from: ex.from,
        to: ex.to,
        answer: ex.answer,
        accepts: ex.accepts,
      };
    case 'reorder':
      return { ...base, type: 'reorder', prompt: ex.prompt, tokens: ex.tokens, answer: ex.answer };
    case 'listen':
      return { ...base, type: 'listen', prompt: ex.prompt, audioText: ex.audioText, answer: ex.answer };
    case 'listen_choice':
      return {
        ...base,
        type: 'listen_choice',
        prompt: ex.prompt,
        audioText: ex.audioText,
        choices: ex.choices,
        answerIndex: ex.answerIndex,
      };
    case 'image_select':
      return {
        ...base,
        type: 'image_select',
        prompt: ex.prompt,
        promptAudio: ex.promptAudio,
        options: ex.options,
        answerIndex: ex.answerIndex,
      };
    case 'speak':
      return { ...base, type: 'speak', prompt: ex.prompt, target: ex.target };
    case 'match_pairs':
      return { ...base, type: 'match_pairs', prompt: ex.prompt, pairs: ex.pairs };
  }
}

function HomeView({
  section,
  unit,
  reviewsDue,
  onStartLesson,
  onSelectUnit,
  lessonStatus,
}: {
  section: Section;
  unit: Unit;
  reviewsDue: string[];
  onStartLesson: (l: Lesson) => void;
  onSelectUnit: (sId: string, uId: string) => void;
  lessonStatus: (u: Unit, l: Lesson, i: number) => 'locked' | 'available' | 'in_progress' | 'completed';
}) {
  const color = UNIT_COLOR_MAP[unit.color] ?? 'rgb(28, 176, 246)';
  return (
    <>
      {reviewsDue.length > 0 ? (
        <div className="sl-mission sl-mission--review">
          <div className="sl-mission__header">
            <span className="sl-mission__icon">
              <Icons.SparkleIcon size={22} />
            </span>
            <div className="sl-mission__text">
              <h3 className="sl-mission__title">
                {reviewsDue.length} {reviewsDue.length === 1 ? 'revisão' : 'revisões'} disponível
              </h3>
              <p className="sl-mission__desc">
                Repetição espaçada ativa — retome lições para fixar o vocabulário (+ XP).
              </p>
            </div>
          </div>
        </div>
      ) : null}

      <UnitBanner
        eyebrow={`SEÇÃO ${section.number}, UNIDADE ${unit.number}`}
        title={unit.title}
        color={color}
      />

      <p className="sl-home-meta">
        <strong>TEMA:</strong> {unit.theme} · {unit.subtitle}
      </p>

      <section className="sl-section" style={{ ['--sl-unit-color' as string]: color }}>
        <PlatformPath
          nodes={unit.lessons.map((l, i) => ({
            id: l.id,
            title: l.title,
            theme: l.popCulture,
            status: lessonStatus(unit, l, i),
            xpReward: l.xpReward,
          }))}
          onStart={(lessonId) => {
            const l = unit.lessons.find((x) => x.id === lessonId);
            if (l) onStartLesson(l);
          }}
        />
      </section>

      <hr className="sl-section-divider" />

      <SectionHeader
        eyebrow="Sua jornada"
        title="Universos pra explorar"
      />

      <div className="sl-galaxy">
        {SECTIONS.map((s, idx) => {
          const activeInSection = s.id === section.id;
          // Heuristic: section is "done" if all its lesson ids are in
          // completedLessons. Since we don't have that here, infer by
          // comparing to active position — past sections are "visited".
          const sectionPos = idx;
          const activePos = SECTIONS.findIndex((x) => x.id === section.id);
          const state: 'visited' | 'current' | 'future' =
            sectionPos < activePos ? 'visited'
              : activeInSection ? 'current'
                : 'future';
          return (
            <article
              key={s.id}
              className={`sl-universe-card sl-universe-card--${state}`}
            >
              <header className="sl-universe-card__header">
                <div className="sl-universe-card__num" aria-hidden>
                  <span>{s.number}</span>
                </div>
                <div className="sl-universe-card__heading">
                  <span className="sl-universe-card__eyebrow">
                    UNIVERSO {s.number} · {s.cefr}
                  </span>
                  <h3 className="sl-universe-card__title">{s.title}</h3>
                  <p className="sl-universe-card__subtitle">{s.subtitle}</p>
                </div>
                <span
                  className={`sl-universe-card__state sl-universe-card__state--${state}`}
                  aria-label={
                    state === 'current'
                      ? 'Universo atual'
                      : state === 'visited'
                        ? 'Universo visitado'
                        : 'Próximo universo'
                  }
                >
                  {state === 'current' ? (
                    <>
                      <Icons.SparkleIcon size={14} /> Atual
                    </>
                  ) : state === 'visited' ? (
                    <>
                      <Icons.CheckIcon size={14} /> Visitado
                    </>
                  ) : (
                    <>Em breve</>
                  )}
                </span>
              </header>

              <div className="sl-universe-card__track" role="list">
                {s.units.map((u, uIdx) => {
                  const isActiveUnit = activeInSection && u.id === unit.id;
                  const isLast = uIdx === s.units.length - 1;
                  const accent = UNIT_COLOR_MAP[u.color] ?? 'var(--sl-accent)';
                  return (
                    <div
                      key={u.id}
                      role="listitem"
                      className="sl-universe-card__node-wrap"
                    >
                      <button
                        type="button"
                        onClick={() => onSelectUnit(s.id, u.id)}
                        className={`sl-universe-card__node${
                          isActiveUnit ? ' sl-universe-card__node--active' : ''
                        }`}
                        style={
                          { ['--sl-unit-accent']: accent } as React.CSSProperties
                        }
                        aria-label={`Unidade ${u.number}: ${u.title}`}
                      >
                        <span className="sl-universe-card__planet" aria-hidden>
                          {renderIllustrationFor(u.theme, 44)}
                        </span>
                        {isActiveUnit ? (
                          <span className="sl-universe-card__here" aria-hidden>
                            <SlinguAvatar size={54} mood="wave" wings />
                            <span className="sl-universe-card__here-pill">
                              Você está aqui
                            </span>
                          </span>
                        ) : null}
                        <span className="sl-universe-card__node-meta">
                          <span className="sl-universe-card__node-num">
                            Unidade {u.number}
                          </span>
                          <span className="sl-universe-card__node-title">
                            {u.title}
                          </span>
                          <span className="sl-universe-card__node-theme">
                            {u.theme}
                          </span>
                        </span>
                      </button>
                      {!isLast ? (
                        <span
                          className="sl-universe-card__bridge"
                          aria-hidden
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>

              <footer className="sl-universe-card__footer">
                <span className="sl-universe-card__count">
                  {s.units.length} {s.units.length === 1 ? 'unidade' : 'unidades'}
                  {' · '}
                  {s.units.reduce((a, u) => a + u.lessons.length, 0)} lições
                </span>
                {state !== 'current' ? (
                  <button
                    type="button"
                    className="sl-btn sl-btn--ghost sl-btn--sm"
                    onClick={() => onSelectUnit(s.id, s.units[0]!.id)}
                  >
                    <span>Explorar</span>
                  </button>
                ) : null}
              </footer>
            </article>
          );
        })}
      </div>
    </>
  );
}

function GamesView({
  onPlayMinigame,
  onPlayQuizRun,
  onPlayCarRacer,
  onPlayWordMatch,
  onPlayWordBuilder,
  onPlayHangman,
  onPlayWordMines,
  onPlayWordSlots,
  onPlayWordSearch,
  onPlayWordJumper,
}: {
  onPlayMinigame: () => void;
  onPlayQuizRun: () => void;
  onPlayCarRacer: () => void;
  onPlayWordMatch: () => void;
  onPlayWordBuilder: () => void;
  onPlayHangman: () => void;
  onPlayWordMines: () => void;
  onPlayWordSlots: () => void;
  onPlayWordSearch: () => void;
  onPlayWordJumper: () => void;
}) {
  const sfx = useSfx();
  return (
    <>
      <SectionHeader
        eyebrow="Jogos"
        title="Aprenda jogando"
      />
      <p className="sl-games__intro">
        Minigames pra treinar vocabulário e reação. Ganhe XP extra enquanto se diverte.
      </p>

      <div className="sl-games__grid">
        <button
          type="button"
          className="sl-game-card sl-game-card--run"
          onClick={() => {
            sfx.play('start');
            onPlayQuizRun();
          }}
        >
          <span className="sl-game-card__art" aria-hidden>
            <span className="sl-game-card__emoji">🏃‍♂️</span>
          </span>
          <span className="sl-game-card__body">
            <span className="sl-game-card__eyebrow">CORRIDA · DIFICULDADE ADAPTATIVA</span>
            <span className="sl-game-card__title">Quiz Run</span>
            <span className="sl-game-card__desc">
              Slingu corre sem parar. A pergunta aparece antes do obstáculo — acertou, ele pula; errou ou demorou, trombou.
            </span>
            <span className="sl-game-card__tags">
              <span className="sl-game-card__tag">+30 XP por acerto</span>
              <span className="sl-game-card__tag">timer 7s</span>
            </span>
          </span>
          <span className="sl-game-card__play">
            <Icons.PlayIcon size={22} />
          </span>
        </button>

        <button
          type="button"
          className="sl-game-card sl-game-card--race"
          onClick={() => {
            sfx.play('start');
            onPlayCarRacer();
          }}
        >
          <span className="sl-game-card__art" aria-hidden>
            <span className="sl-game-card__emoji">🏎️</span>
          </span>
          <span className="sl-game-card__body">
            <span className="sl-game-card__eyebrow">CORRIDA · 6 TEMAS</span>
            <span className="sl-game-card__title">Slingu Racer</span>
            <span className="sl-game-card__desc">
              Dirija o carro arrastando o dedo. Passe sobre a palavra certa pra acelerar — bater na errada, o carro explode.
            </span>
            <span className="sl-game-card__tags">
              <span className="sl-game-card__tag">+18 XP por acerto</span>
              <span className="sl-game-card__tag">3 explosões = fim</span>
            </span>
          </span>
          <span className="sl-game-card__play">
            <Icons.PlayIcon size={22} />
          </span>
        </button>

        <button
          type="button"
          className="sl-game-card sl-game-card--flap"
          onClick={() => {
            sfx.play('start');
            onPlayMinigame();
          }}
        >
          <span className="sl-game-card__art" aria-hidden>
            <span className="sl-game-card__emoji">🪽</span>
          </span>
          <span className="sl-game-card__body">
            <span className="sl-game-card__eyebrow">VOCABULÁRIO · 6 TEMAS</span>
            <span className="sl-game-card__title">Flappy Slingu</span>
            <span className="sl-game-card__desc">
              Escolha um tema (animais, comida, verbos…) e voe tocando na tradução certa em inglês.
            </span>
            <span className="sl-game-card__tags">
              <span className="sl-game-card__tag">+15 XP por acerto</span>
              <span className="sl-game-card__tag">do A1 ao B1+</span>
            </span>
          </span>
          <span className="sl-game-card__play">
            <Icons.PlayIcon size={22} />
          </span>
        </button>

        <button
          type="button"
          className="sl-game-card sl-game-card--match"
          onClick={() => {
            sfx.play('start');
            onPlayWordMatch();
          }}
        >
          <span className="sl-game-card__art" aria-hidden>
            <span className="sl-game-card__emoji">🃏</span>
          </span>
          <span className="sl-game-card__body">
            <span className="sl-game-card__eyebrow">MEMÓRIA · PARES PT↔EN</span>
            <span className="sl-game-card__title">Word Match</span>
            <span className="sl-game-card__desc">
              Vire as cartas e ache os pares português e inglês. Quanto mais rápido, mais XP de bônus.
            </span>
            <span className="sl-game-card__tags">
              <span className="sl-game-card__tag">+10 XP por par</span>
              <span className="sl-game-card__tag">4 rodadas</span>
            </span>
          </span>
          <span className="sl-game-card__play">
            <Icons.PlayIcon size={22} />
          </span>
        </button>

        <button
          type="button"
          className="sl-game-card sl-game-card--build"
          onClick={() => {
            sfx.play('start');
            onPlayWordBuilder();
          }}
        >
          <span className="sl-game-card__art" aria-hidden>
            <span className="sl-game-card__emoji">🧩</span>
          </span>
          <span className="sl-game-card__body">
            <span className="sl-game-card__eyebrow">GRAMÁTICA · ORDENAR</span>
            <span className="sl-game-card__title">Word Builder</span>
            <span className="sl-game-card__desc">
              Monte a frase em inglês na ordem certa, toque nas palavras pra encaixar. Treina gramática e estrutura.
            </span>
            <span className="sl-game-card__tags">
              <span className="sl-game-card__tag">+20 XP por frase</span>
              <span className="sl-game-card__tag">10 frases</span>
            </span>
          </span>
          <span className="sl-game-card__play">
            <Icons.PlayIcon size={22} />
          </span>
        </button>

        <button type="button" className="sl-game-card sl-game-card--hangman" onClick={() => { sfx.play('start'); onPlayHangman(); }}>
          <span className="sl-game-card__art" aria-hidden><span className="sl-game-card__emoji">🪢</span></span>
          <span className="sl-game-card__body">
            <span className="sl-game-card__eyebrow">FORCA · LETRA POR LETRA</span>
            <span className="sl-game-card__title">Hangman</span>
            <span className="sl-game-card__desc">
              Descubra a palavra em inglês antes que o Slingu enforque. 6 erros de letra = fim. 8 rodadas por jogo.
            </span>
            <span className="sl-game-card__tags">
              <span className="sl-game-card__tag">+18 XP por palavra</span>
              <span className="sl-game-card__tag">bônus por precisão</span>
            </span>
          </span>
          <span className="sl-game-card__play"><Icons.PlayIcon size={22} /></span>
        </button>

        <button type="button" className="sl-game-card sl-game-card--mines" onClick={() => { sfx.play('start'); onPlayWordMines(); }}>
          <span className="sl-game-card__art" aria-hidden><span className="sl-game-card__emoji">💣</span></span>
          <span className="sl-game-card__body">
            <span className="sl-game-card__eyebrow">CAMPO MINADO · CUIDADO</span>
            <span className="sl-game-card__title">Word Mines</span>
            <span className="sl-game-card__desc">
              25 tiles, algumas com minas. Toque só na tradução correta. Mina = -2♥, palavra errada = -1♥.
            </span>
            <span className="sl-game-card__tags">
              <span className="sl-game-card__tag">+15 XP por acerto</span>
              <span className="sl-game-card__tag">6 rodadas</span>
            </span>
          </span>
          <span className="sl-game-card__play"><Icons.PlayIcon size={22} /></span>
        </button>

        <button type="button" className="sl-game-card sl-game-card--slots" onClick={() => { sfx.play('start'); onPlayWordSlots(); }}>
          <span className="sl-game-card__art" aria-hidden><span className="sl-game-card__emoji">🎰</span></span>
          <span className="sl-game-card__body">
            <span className="sl-game-card__eyebrow">CASSINO · 3 REELS</span>
            <span className="sl-game-card__title">Word Slots</span>
            <span className="sl-game-card__desc">
              Gire os reels e pare cada um na tradução correta. 3/3 = JACKPOT 80 XP. 10 giros por partida.
            </span>
            <span className="sl-game-card__tags">
              <span className="sl-game-card__tag">+20 XP por reel</span>
              <span className="sl-game-card__tag">jackpot 80 XP</span>
            </span>
          </span>
          <span className="sl-game-card__play"><Icons.PlayIcon size={22} /></span>
        </button>

        <button type="button" className="sl-game-card sl-game-card--search" onClick={() => { sfx.play('start'); onPlayWordSearch(); }}>
          <span className="sl-game-card__art" aria-hidden><span className="sl-game-card__emoji">🔍</span></span>
          <span className="sl-game-card__body">
            <span className="sl-game-card__eyebrow">CAÇA PALAVRAS · 10×10</span>
            <span className="sl-game-card__title">Word Search</span>
            <span className="sl-game-card__desc">
              Arraste o dedo sobre as letras e ache 6 palavras em inglês escondidas no grid. Horizontal, vertical, diagonal.
            </span>
            <span className="sl-game-card__tags">
              <span className="sl-game-card__tag">+18 XP por palavra</span>
              <span className="sl-game-card__tag">sem cronômetro</span>
            </span>
          </span>
          <span className="sl-game-card__play"><Icons.PlayIcon size={22} /></span>
        </button>

        <button type="button" className="sl-game-card sl-game-card--jump" onClick={() => { sfx.play('start'); onPlayWordJumper(); }}>
          <span className="sl-game-card__art" aria-hidden><span className="sl-game-card__emoji">🦘</span></span>
          <span className="sl-game-card__body">
            <span className="sl-game-card__eyebrow">PLATAFORMA · PULO PRECISO</span>
            <span className="sl-game-card__title">Word Jumper</span>
            <span className="sl-game-card__desc">
              Pule entre plataformas flutuantes e pouse na com a tradução certa. Errou, explode.
            </span>
            <span className="sl-game-card__tags">
              <span className="sl-game-card__tag">+18 XP por pouso</span>
              <span className="sl-game-card__tag">3 explosões = fim</span>
            </span>
          </span>
          <span className="sl-game-card__play"><Icons.PlayIcon size={22} /></span>
        </button>
      </div>

      <div className="sl-games__footer">
        <Icons.SparkleIcon size={18} />
        <span>Mais jogos em breve.</span>
      </div>
    </>
  );
}

function MissionsView({ progress, reviewsDue }: { progress: Progress; reviewsDue: number }) {
  const dailyXpTarget = 50;
  const dailyProgress = Math.min(progress.xp, dailyXpTarget);
  const lessonCount = progress.completedLessons.length;
  return (
    <>
      <SectionHeader eyebrow="Missões · Hoje" title="Continue a sequência!" />
      <section className="sl-section sl-section--grid">
        <MissionCard
          icon={<Icons.XpBoltIcon size={22} />}
          title="Ganhe 50 XP"
          description="Complete qualquer combinação de lições."
          progress={dailyProgress}
          target={dailyXpTarget}
          rewardXp={10}
          rewardGems={5}
          completed={dailyProgress >= dailyXpTarget}
        />
        <MissionCard
          icon={<Icons.FlameIcon size={22} />}
          title="3 dias seguidos"
          description="Volte amanhã para manter a chama."
          progress={progress.streak}
          target={3}
          rewardXp={25}
          rewardGems={10}
        />
        <MissionCard
          icon={<Icons.SparkleIcon size={22} />}
          title="Revisões abertas"
          description="Fixe vocabulário com repetição espaçada."
          progress={reviewsDue}
          target={Math.max(reviewsDue, 3)}
          rewardXp={35}
          rewardGems={12}
        />
        <MissionCard
          icon={<Icons.BookIcon size={22} />}
          title="5 lições concluídas"
          description="Lições de qualquer unidade."
          progress={Math.min(5, lessonCount)}
          target={5}
          rewardXp={30}
          rewardGems={15}
          completed={lessonCount >= 5}
        />
        <MissionCard
          icon={<Icons.MicIcon size={22} />}
          title="Mestre da pronúncia"
          description="Acerte 10 exercícios de fala."
          progress={2}
          target={10}
          rewardXp={50}
          rewardGems={20}
        />
        <MissionCard
          icon={<Icons.HeadphonesIcon size={22} />}
          title="Audição afiada"
          description="Acerte 8 exercícios de ouvir."
          progress={1}
          target={8}
          rewardXp={35}
          rewardGems={12}
        />
      </section>
    </>
  );
}

function LeaguesView({ xp }: { xp: number }) {
  const myTier = useMemo(() => {
    const idx = Math.min(LEAGUE_TIERS.length - 1, Math.floor(xp / 200));
    return LEAGUE_TIERS[idx]!;
  }, [xp]);
  const rows = useMemo(
    () =>
      [
        { name: 'Você', xp, me: true },
        { name: 'Naruto-fan', xp: 500 },
        { name: 'L-chan', xp: 460 },
        { name: 'GokuBR', xp: 420 },
        { name: 'Eren', xp: 370 },
        { name: 'Saitama', xp: 320 },
      ].sort((a, b) => b.xp - a.xp),
    [xp],
  );
  return (
    <>
      <SectionHeader
        eyebrow="Ligas · Semana atual"
        title={`Liga ${myTier.label}`}
        trailing={
          <span className="sl-league-tier" style={{ background: myTier.color }}>
            <Icons.TrophyIcon size={14} /> {myTier.label}
          </span>
        }
      />
      <section className="sl-section">
        <ol className="sl-league">
          {rows.map((row, i) => (
            <li key={row.name} className={`sl-league__row${row.me ? ' sl-league__row--me' : ''}`}>
              <span className="sl-league__rank">{i + 1}º</span>
              <span className="sl-league__name">{row.name}</span>
              <span className="sl-league__xp">
                <Icons.XpBoltIcon size={14} /> {row.xp}
              </span>
            </li>
          ))}
        </ol>
        <p className="sl-league-footer">
          No final da semana, o top 3 sobe para {myTier.label === 'Champion' ? 'o topo' : 'a próxima liga'}.
        </p>
      </section>
    </>
  );
}

function ProfileView({ user, progress }: { user: SessionUser | null; progress: Progress }) {
  const level = computeLevel(progress.xp);
  const totalLessons = SECTIONS.reduce(
    (a, s) => a + s.units.reduce((b, u) => b + u.lessons.length, 0),
    0,
  );
  const weakSpotsCount = Object.keys(progress.weakSpots).length;
  return (
    <>
      <SectionHeader eyebrow="Perfil" title="Seu progresso" />
      <section className="sl-section sl-section--grid">
        <div className="sl-mission">
          <div className="sl-mission__header">
            <span className="sl-mission__icon">
              <Icons.XpBoltIcon size={22} />
            </span>
            <div className="sl-mission__text">
              <h3 className="sl-mission__title">{progress.xp} XP</h3>
              <p className="sl-mission__desc">
                Nível {level} · próximo em {100 - (progress.xp % 100)} XP
              </p>
            </div>
          </div>
        </div>
        <div className="sl-mission">
          <div className="sl-mission__header">
            <span className="sl-mission__icon" style={{ color: 'var(--sl-streak)' }}>
              <Icons.FlameIcon size={22} />
            </span>
            <div className="sl-mission__text">
              <h3 className="sl-mission__title">{progress.streak} dias</h3>
              <p className="sl-mission__desc">Sua maior sequência atual</p>
            </div>
          </div>
        </div>
        <div className="sl-mission">
          <div className="sl-mission__header">
            <span className="sl-mission__icon">
              <Icons.CheckIcon size={22} />
            </span>
            <div className="sl-mission__text">
              <h3 className="sl-mission__title">
                {progress.completedLessons.length}/{totalLessons}
              </h3>
              <p className="sl-mission__desc">Lições concluídas</p>
            </div>
          </div>
        </div>
        <div className="sl-mission">
          <div className="sl-mission__header">
            <span className="sl-mission__icon" style={{ color: 'var(--sl-new-word)' }}>
              <Icons.SparkleIcon size={22} />
            </span>
            <div className="sl-mission__text">
              <h3 className="sl-mission__title">{weakSpotsCount} weak spots</h3>
              <p className="sl-mission__desc">
                Exercícios que ainda estão pegando você — revise!
              </p>
            </div>
          </div>
        </div>
      </section>

      <hr className="sl-section-divider" />

      <SectionHeader eyebrow="Conta" title={user?.email ?? 'Convidado'} />
      <section className="sl-section">
        <div className="sl-profile-actions">
          <Button onClick={() => window.location.reload()}>Atualizar</Button>
          <Button
            variant="secondary"
            onClick={() => {
              localStorage.removeItem(PROGRESS_KEY);
              window.location.reload();
            }}
          >
            Reiniciar progresso
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              void fetch('/api/v1/auth/logout', {
                method: 'POST',
                credentials: 'include',
              })
                .catch(() => null)
                .finally(() => window.location.reload());
            }}
          >
            Sair
          </Button>
        </div>
        <details className="sl-profile-debug">
          <summary>Debug / JSON</summary>
          <pre className="sl-debug">{JSON.stringify({ user, progress }, null, 2)}</pre>
        </details>
      </section>
    </>
  );
}
